import React, { useState } from 'react'
import { ChevronDown, Lightbulb, ArrowRight } from 'lucide-react'
import { guideFor } from '../lib/productGuides'

/**
 * Example-driven "How it works" panel for a product.
 * - On manager pages: pass `defaultOpen` (expanded teaching banner).
 * - In the drawer: pass `collapsible` for a compact expandable.
 */
export default function ProductGuide({ product, defaultOpen = true, collapsible = false }) {
  const g = guideFor(product)
  const [open, setOpen] = useState(defaultOpen)
  if (!g) return null

  const Body = (
    <div className="grid md:grid-cols-2 gap-5">
      {/* how it works */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3">How it works</p>
        <ol className="space-y-3">
          {g.how.map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-primary-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="text-sm font-semibold text-gray-900">{s.t}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* worked example */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-1.5">
          <Lightbulb size={13} className="text-amber-500" /> Example
        </p>
        <div className="rounded-lg bg-white border border-gray-200 p-4">
          <p className="text-sm text-gray-700 italic mb-3">{g.example.scenario}</p>
          <div className="space-y-3">
            {g.example.steps.map((st, i) => (
              <div key={i} className="text-sm">
                <p className="text-gray-900 font-medium flex gap-1.5">
                  <span className="text-primary-600 font-bold tabular-nums">{i + 1}.</span>{st.do}
                </p>
                <p className="text-gray-500 flex gap-1.5 mt-0.5 pl-4">
                  <ArrowRight size={14} className="flex-none mt-0.5 text-gray-400" />{st.then}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-100/60 overflow-hidden">
      <button
        onClick={() => collapsible && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left ${collapsible ? 'hover:bg-gray-100' : 'cursor-default'}`}>
        <div>
          <p className="text-sm font-bold text-gray-900">How {g.name} works</p>
          <p className="text-xs text-gray-500">{g.tagline}</p>
        </div>
        {collapsible && <ChevronDown size={18} className={`flex-none text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && <div className="px-5 pb-5 pt-1">{Body}</div>}
    </div>
  )
}
